import { MapTrifold, ArrowsOut, X, ArrowSquareOut } from '@phosphor-icons/react';
import { cadastreBelongsToQuery } from '@/utils/cadastreCache';

/** Official map provider: { url, label } when status ok + EGRID for GE / VD / FR. */
export function resolveOfficialCadastreProvider(cadastreData, queryAddress = null) {
  if (queryAddress != null && String(queryAddress).trim() !== '') {
    if (!cadastreBelongsToQuery(queryAddress, cadastreData)) return null;
  }
  if (cadastreData?.status !== 'ok') return null;
  const egrid = typeof cadastreData?.egrid === 'string' ? cadastreData.egrid.trim() : '';
  if (!egrid) return null;
  const canton = String(cadastreData?.canton || '').toUpperCase();
  const id = encodeURIComponent(egrid);
  if (canton === 'VD') {
    return { url: `https://www.rdppf.vd.ch/portail.aspx?egrid=${id}`, label: 'Source officielle VD' };
  }
  if (canton === 'GE') {
    return {
      url: `https://map.sitg.ge.ch/app/?mapresources=CADASTRE_RDPPF&locegrid=${id}&keepgraphic=true`,
      label: 'Source officielle Genève (SITG)',
    };
  }
  if (canton === 'FR') {
    return {
      url: `https://maps.fr.ch/cadastre_rdppf/?lang=fr&egrid=${id}`,
      label: 'Source officielle Fribourg (RDPPF)',
    };
  }
  return null;
}

/** Google Earth 3D — opens in a new tab only (not embeddable). */
export function earthGoogleUrl(address) {
  if (!address) return null;
  return `https://earth.google.com/web/search/${encodeURIComponent(address)}`;
}

/**
 * External URL for “Open current view” (embedded modes only).
 * Satellite → Google Maps 2D satellite; Cadastre → official VD/GE portal.
 */
export function resolveOpenViewUrl(mode, address, cadastreData) {
  if (!address) return null;
  const q = encodeURIComponent(address);
  if (mode === 'cadastre') {
    return resolveOfficialCadastreProvider(cadastreData, address)?.url || null;
  }
  return `https://www.google.com/maps?q=${q}&t=k&z=17`;
}

/** In-app Satellite embed: Google Maps satellite imagery (2D). */
function satelliteEmbedUrl(address) {
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=k&z=17&output=embed`;
}

function cadastreContextLine(cadastreData, provider) {
  if (!provider || !cadastreData) return null;
  const parcel = String(cadastreData.parcel_number || '').trim();
  const m2 = Math.round(Number(cadastreData.surface_m2));
  const surface = Number.isFinite(m2) && m2 > 0
    ? `${String(m2).replace(/\B(?=(\d{3})+(?!\d))/g, '\u2019')} m\u00b2`
    : null;
  return [
    parcel ? `Parcelle ${parcel}` : null,
    `EGRID ${String(cadastreData.egrid).trim()}`,
    surface,
    provider.label,
  ].filter(Boolean).join(' · ');
}

function tabClass(active, disabled = false) {
  if (disabled) return 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed';
  return active
    ? 'bg-slate-800 text-white'
    : 'bg-white text-slate-600 border border-slate-200';
}

function MapViewMenu({ mode, onModeChange, provider, cadastreData, address }) {
  const unavailable = !provider
    ? 'Cadastre officiel indisponible — réponse ok et EGRID requis (GE / VD / FR).'
    : '';
  const openUrl = resolveOpenViewUrl(mode, address, cadastreData);
  const earthUrl = earthGoogleUrl(address);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="map-mode-tabs">
      <button type="button" onClick={() => onModeChange('satellite')} data-testid="map-mode-satellite"
        aria-pressed={mode === 'satellite'}
        title="Google Maps — satellite imagery (2D)"
        className={`px-2 py-0.5 rounded text-[9px] font-semibold min-h-[24px] ${tabClass(mode === 'satellite')}`}>
        Satellite
      </button>
      {earthUrl && (
        <a
          href={earthUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="map-mode-earth"
          title="Open Google Earth 3D in a new tab"
          className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold min-h-[24px] ${tabClass(false)}`}
        >
          Earth
        </a>
      )}
      <button type="button" data-testid="map-mode-cadastre" aria-pressed={mode === 'cadastre'}
        disabled={!provider} title={provider ? provider.label : unavailable}
        onClick={() => { if (provider) onModeChange('cadastre'); }}
        className={`px-2 py-0.5 rounded text-[9px] font-semibold min-h-[24px] ${tabClass(mode === 'cadastre', !provider)}`}>
        Cadastre officiel
      </button>
      {openUrl && (
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[9px] font-semibold min-h-[24px] text-blue-700 hover:text-blue-800 border border-blue-200 bg-white"
          data-testid="map-open-current-view"
        >
          Open current view
          <ArrowSquareOut weight="bold" className="w-3 h-3" />
        </a>
      )}
      {!provider && <span className="text-[8px] text-slate-400" data-testid="map-cadastre-unavailable">{unavailable}</span>}
    </div>
  );
}

function OfficialContextBar({ cadastreData, provider }) {
  const context = cadastreContextLine(cadastreData, provider);
  if (!context || !provider) return null;
  return (
    <div className="text-[9px] text-slate-600 leading-snug" data-testid="map-cadastre-context">
      {context}
    </div>
  );
}

function MapStage({ mode, address, provider, onFullscreen, preview }) {
  const showCadastre = mode === 'cadastre' && provider;
  const previewH = preview ? (showCadastre ? 'h-[300px]' : 'h-[160px]') : 'h-full';
  const frameTestId = showCadastre
    ? (preview ? 'map-cadastre-iframe' : 'map-fullscreen-cadastre-iframe')
    : (preview ? 'map-satellite-iframe' : 'map-fullscreen-satellite-iframe');

  return (
    <div className={`relative w-full ${previewH} ${preview ? 'rounded-lg overflow-hidden border border-slate-200 bg-slate-100' : 'h-full bg-slate-100'}`} data-testid={preview ? 'map-preview' : undefined}>
      {showCadastre ? (
        <iframe
          title="Cadastre officiel"
          src={provider.url}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="fullscreen"
          allowFullScreen
          data-testid={frameTestId}
        />
      ) : (
        <iframe
          title="Google Maps satellite (2D)"
          src={satelliteEmbedUrl(address)}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="fullscreen"
          allowFullScreen
          data-testid={frameTestId}
        />
      )}
      {preview && onFullscreen && (
        <button type="button" onClick={onFullscreen}
          className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white border border-slate-300 rounded-md p-1.5 min-h-[28px] min-w-[28px] shadow-sm transition-colors"
          data-testid="map-fullscreen-btn" title="Expand map" aria-label="Expand map">
          <ArrowsOut weight="bold" className="w-3.5 h-3.5 text-slate-600" />
        </button>
      )}
    </div>
  );
}

export function LandMap({
  address,
  onFullscreen,
  cadastreData = null,
  mode = 'satellite',
  onModeChange,
}) {
  const provider = resolveOfficialCadastreProvider(cadastreData, address);
  const setMode = typeof onModeChange === 'function' ? onModeChange : () => {};
  const embedMode = mode === 'cadastre' ? 'cadastre' : 'satellite';

  if (!address) {
    return (
      <div className="w-full h-[160px] rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center" data-testid="map-empty">
        <div className="text-center">
          <MapTrifold weight="duotone" className="w-8 h-8 text-slate-300 mx-auto mb-1" />
          <span className="text-[10px] text-slate-400">Enter an address to view map</span>
        </div>
      </div>
    );
  }

  const showCadastre = embedMode === 'cadastre' && provider;

  return (
    <>
      <div className="mb-1">
        <MapViewMenu mode={embedMode} onModeChange={setMode} provider={provider} cadastreData={cadastreData} address={address} />
      </div>
      {showCadastre && (
        <div className="mb-1">
          <OfficialContextBar cadastreData={cadastreData} provider={provider} />
        </div>
      )}
      <MapStage mode={embedMode} address={address} provider={provider} onFullscreen={onFullscreen} preview />
    </>
  );
}

export function MapFullscreen({
  address,
  onClose,
  cadastreData = null,
  mode = 'satellite',
  onModeChange,
}) {
  if (!address) return null;

  const provider = resolveOfficialCadastreProvider(cadastreData, address);
  const setMode = typeof onModeChange === 'function' ? onModeChange : () => {};
  const embedMode = mode === 'cadastre' ? 'cadastre' : 'satellite';
  const showCadastre = embedMode === 'cadastre' && provider;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-8" onClick={onClose} data-testid="map-fullscreen-overlay">
      <div className="relative w-full h-full max-w-[90vw] max-h-[85vh] rounded-lg overflow-hidden shadow-2xl bg-white flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 px-3 py-2 border-b border-slate-200 space-y-1 bg-white">
          <MapViewMenu mode={embedMode} onModeChange={setMode} provider={provider} cadastreData={cadastreData} address={address} />
          {showCadastre && <OfficialContextBar cadastreData={cadastreData} provider={provider} />}
        </div>
        <div className="relative flex-1 min-h-0">
          <MapStage mode={embedMode} address={address} provider={provider} preview={false} />
          <button type="button" onClick={onClose}
            className="absolute top-3 right-3 bg-white hover:bg-slate-100 border border-slate-300 rounded-full p-1.5 min-h-[32px] min-w-[32px] shadow-lg transition-colors"
            data-testid="map-fullscreen-close" title="Close map" aria-label="Close map">
            <X weight="bold" className="w-4 h-4 text-slate-700" />
          </button>
        </div>
      </div>
    </div>
  );
}
